/// <amd-module name="Repos/ChangesActions/ChangesActionsService" />
import * as React from "react";
import { IChangesActionsService } from "Repos/ChangesActions/Contracts";
import * as Resources from "Repos/ChangesActions/Resources";
import {
    GitAsyncOperationStatus,
    GitAsyncRefOperation,
    GitAsyncRefOperationSource,
    GitCommitRef,
    GitPullRequest,
    GitPullRequestMergeStrategy,
    GitRepository
} from "Repos/Common/Contracts/Git";
import { IReposRoutingService } from "Repos/Common/ReposRoutingService";
import { branchNameToRef, getShortCommitId } from "Repos/Common/Util/Ref";
import { branchToVersionDescriptor } from "Repos/Common/Util/Version";
import { IReposMessagesService } from "Repos/Messages/ReposMessagesService";
import { IGitRefService } from "Repos/VersionDropdown/GitRefService";
import { ObservableValue } from "VSS/Core/Observable";
import { wait } from "VSS/Core/Util/Promise";
import { format } from "VSS/Core/Util/String";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { Services, VssService } from "VSS/Platform/Context";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { Icon } from "VSSUI/Icon";
import { IPortChangesCompletionOptions, PortChangesPanel } from "./Components/PortChangesPanel";
import { getGitClient } from "./RestClient/Git";

class ChangesActionsService extends VssService implements IChangesActionsService {
    public cherryPickPullRequest(
        pullRequest: GitPullRequest,
        repository: GitRepository,
        pullRequestSourceBranch: string,
        pullRequestTargetBranch: string
    ): void {
        const pullRequestId = pullRequest.pullRequestId;
        const lastMergeCommitId = pullRequest.lastMergeCommit ? pullRequest.lastMergeCommit.commitId : undefined;
        const showCherryPickCheckbox =
            !!lastMergeCommitId &&
            !(pullRequest.completionOptions && pullRequest.completionOptions.mergeStrategy === GitPullRequestMergeStrategy.Rebase);

        this.cherryPick(
            format(Resources.CherryPickPullRequestTitle, pullRequestId),
            {
                pullRequestId
            } as GitAsyncRefOperationSource,
            repository,
            pullRequestSourceBranch,
            pullRequestTargetBranch,
            showCherryPickCheckbox,
            lastMergeCommitId
        );
    }

    public cherryPickCommit(commitId: string, repository: GitRepository): void {
        const commitList: GitCommitRef[] = [{ commitId: commitId } as GitCommitRef];
        this.cherryPick(
            format(Resources.CherryPickCommitTitle, getShortCommitId(commitId)),
            {
                commitList: commitList
            } as GitAsyncRefOperationSource,
            repository,
            getShortCommitId(commitId),
            undefined
        );
    }

    public revertPullRequest(
        pullRequestId: number,
        repository: GitRepository,
        pullRequestSourceBranch: string,
        pullRequestTargetBranch: string
    ): void {
        this.revert(
            format(Resources.RevertPullRequestTitle, pullRequestId),
            Resources.RevertPullRequestDescription,
            {
                pullRequestId
            } as GitAsyncRefOperationSource,
            repository,
            pullRequestSourceBranch,
            pullRequestTargetBranch
        );
    }

    public revertCommit(commitId: string, repository: GitRepository, targetBranch: string | undefined): void {
        const commitList: GitCommitRef[] = [{ commitId: commitId } as GitCommitRef];
        this.revert(
            format(Resources.RevertCommitTitleFormat, getShortCommitId(commitId)),
            Resources.RevertCommitDescription,
            {
                commitList: commitList
            } as GitAsyncRefOperationSource,
            repository,
            getShortCommitId(commitId),
            targetBranch
        );
    }

    public revert(
        panelTitle: string,
        description: string,
        gitAsyncRefOperationSource: GitAsyncRefOperationSource,
        repository: GitRepository,
        sourceBranch: string,
        targetBranch: string | undefined
    ): void {
        const errorMessage = new ObservableValue("");
        const pctComplete = new ObservableValue(0);
        const showOverlay = new ObservableValue(false);
        const refService = this.pageContext.getService<IGitRefService>("IGitRefService");

        this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager").renderCallout((onDismiss: () => void) => {
            return (
                <PortChangesPanel
                    getBranchByName={branchName => refService.getBranchByName(repository.id, repository.project.id, branchName)}
                    errorMessage={errorMessage}
                    initialTargetBranch={targetBranch}
                    inProgressLabel={Resources.InProgressRevert}
                    panelCompleteButtonText={Resources.Revert}
                    panelDescription={description}
                    panelTitle={panelTitle}
                    pctComplete={pctComplete}
                    showOverlay={showOverlay}
                    sourceBranch={sourceBranch}
                    topicBranchNameFormat={Resources.RevertBranchFormat}
                    onDismiss={async details => {
                        if (!details) {
                            onDismiss();
                            return;
                        }

                        errorMessage.value = "";
                        showOverlay.value = true;
                        const gitClient = getGitClient(this.pageContext);

                        try {
                            const revert = await gitClient.createRevert(
                                {
                                    generatedRefName: branchNameToRef(details.topicBranchName),
                                    ontoRefName: branchNameToRef(details.targetBranch),
                                    repository: repository,
                                    source: gitAsyncRefOperationSource
                                },
                                repository.project.id,
                                repository.id
                            );

                            await this.waitForStatus(
                                () => gitClient.getRevert(repository.project.id, revert.revertId, repository.id),
                                pctComplete,
                                Resources.RevertErrorFormat,
                                Resources.RevertConflictsMessage
                            );
                        } catch (ex) {
                            showOverlay.value = false;
                            errorMessage.value = ex.message;
                            return;
                        }

                        // Dimiss the panel
                        onDismiss();

                        this.navigateToCreatePullRequest(
                            repository,
                            details,
                            Resources.RevertPullRequestCompletedFormat,
                            0,
                            gitAsyncRefOperationSource.pullRequestId,
                            "",
                            gitAsyncRefOperationSource.commitList && gitAsyncRefOperationSource.commitList[0]?.commitId
                        );
                    }}
                />
            );
        });
    }

    private cherryPick(
        panelTitle: string,
        gitAsyncRefOperationSource: GitAsyncRefOperationSource,
        repository: GitRepository,
        sourceBranch: string,
        targetBranch: string | undefined,
        showCherryPickCheckbox?: boolean,
        lastMergeCommitId?: string
    ): void {
        const errorMessage = new ObservableValue("");
        const pctComplete = new ObservableValue(0);
        const showOverlay = new ObservableValue(false);
        const refService = this.pageContext.getService<IGitRefService>("IGitRefService");
        const cherryPickAsSingleCommit = new ObservableValue<boolean>(false);

        this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager").renderCallout((onDismiss: () => void) => {
            return (
                <PortChangesPanel
                    cherryPickAsSingleCommit={cherryPickAsSingleCommit}
                    getBranchByName={branchName => refService.getBranchByName(repository.id, repository.project.id, branchName)}
                    errorMessage={errorMessage}
                    initialTargetBranch={targetBranch}
                    inProgressLabel={Resources.InProgressCherryPick}
                    panelCompleteButtonText={Resources.CherryPick}
                    panelDescription={Resources.CherryPickDescription}
                    panelTitle={panelTitle}
                    pctComplete={pctComplete}
                    showOverlay={showOverlay}
                    sourceBranch={sourceBranch}
                    showCherryPickCheckbox={showCherryPickCheckbox}
                    topicBranchNameFormat={Resources.CherryPickBranchFormat}
                    onDismiss={async details => {
                        if (!details) {
                            onDismiss();
                            return;
                        }

                        const pullRequestId = gitAsyncRefOperationSource.pullRequestId;

                        if (cherryPickAsSingleCommit.value && lastMergeCommitId) {
                            gitAsyncRefOperationSource = {
                                commitList: [{ commitId: lastMergeCommitId } as GitCommitRef]
                            } as GitAsyncRefOperationSource;
                        }

                        errorMessage.value = "";
                        showOverlay.value = true;
                        const gitClient = getGitClient(this.pageContext);

                        try {
                            const cherryPick = await gitClient.createCherryPick(
                                {
                                    generatedRefName: branchNameToRef(details.topicBranchName),
                                    ontoRefName: branchNameToRef(details.targetBranch),
                                    repository: repository,
                                    source: gitAsyncRefOperationSource
                                },
                                repository.project.id,
                                repository.id
                            );

                            await this.waitForStatus(
                                () => gitClient.getCherryPick(repository.project.id, cherryPick.cherryPickId, repository.id),
                                pctComplete,
                                Resources.CherryPickErrorFormat,
                                Resources.CherryPickConflictsMessage
                            );
                        } catch (ex) {
                            showOverlay.value = false;
                            errorMessage.value = ex.message;
                            return;
                        }

                        // Dimiss the panel
                        onDismiss();

                        this.navigateToCreatePullRequest(
                            repository,
                            details,
                            Resources.CherryPickPullRequestCompletedFormat,
                            pullRequestId,
                            0,
                            gitAsyncRefOperationSource.commitList && gitAsyncRefOperationSource.commitList[0]?.commitId,
                            ""
                        );
                    }}
                />
            );
        });
    }

    private async waitForStatus(
        getStatus: () => Promise<GitAsyncRefOperation>,
        percentComplete: ObservableValue<number>,
        errorFormat: string,
        conflictFormat: string
    ): Promise<void> {
        let checkIndex = 0;
        while (true) {
            const statusCheck = await getStatus();
            if (statusCheck.status === GitAsyncOperationStatus.Queued || statusCheck.status === GitAsyncOperationStatus.InProgress) {
                if (statusCheck.detailedStatus) {
                    percentComplete.value = statusCheck.detailedStatus.progress;
                }
                // Ping every 1 second up to 10 seconds, then every 5 seconds up to 3 minutes. Then every 30 seconds.
                const waitTime = ++checkIndex < 10 ? 1000 : checkIndex > 40 ? 30000 : 5000;
                await wait(waitTime);
            } else if (statusCheck.status !== GitAsyncOperationStatus.Completed) {
                let message = format(errorFormat, statusCheck.detailedStatus.failureMessage);
                if (statusCheck.detailedStatus.conflict) {
                    message = format(conflictFormat, statusCheck.detailedStatus.currentCommitId);
                }
                throw new Error(message);
            } else {
                break;
            }
        }
    }

    private navigateToCreatePullRequest(
        repository: GitRepository,
        details: IPortChangesCompletionOptions,
        completeFormat: string,
        cherryPickId: number,
        revertId: number,
        cherryPickCommit: string,
        revertCommit: string
    ): void {
        const routeService = this.pageContext.getService<IReposRoutingService>("IReposRoutingService");

        const reposMessagesService = this.pageContext.getService<IReposMessagesService>("IReposMessagesService");
        reposMessagesService.showMessage(
            {
                renderMessage: () => (
                    <FormatComponent format={completeFormat}>
                        <span>
                            <Icon iconName="OpenSource" className="margin-right-4" />
                            <FPSLink
                                href={routeService.getFilesUrl(
                                    repository.name,
                                    repository.project.name,
                                    undefined,
                                    branchToVersionDescriptor(details.topicBranchName)
                                )}
                            >
                                {details.topicBranchName}
                            </FPSLink>
                        </span>
                    </FormatComponent>
                )
            },
            true
        );

        // Navigate to the create pull request page
        const pullRequestUrl = routeService.getPullRequestCreateUrl(
            repository.name,
            repository.project.name,
            details.topicBranchName,
            details.targetBranch,
            repository.id,
            repository.id,
            cherryPickId,
            revertId,
            cherryPickCommit,
            revertCommit
        );
        FastPageSwitch(this.pageContext, pullRequestUrl, true);
    }
}

Services.add("IChangesActionsService", { serviceFactory: ChangesActionsService });
